import { Suspense, useEffect, useMemo } from 'react';

import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import { BpkBox, BpkSpacing } from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { GlobalNavigation } from '@web-platform/homepage-features-global-navigation';
import {
  EventsGraphicPromo,
  GraphicPromoFallbackCard,
  GraphicPromoCardController,
} from '@web-platform/homepage-features-graphic-promo';
import { HomepageHeroContainer } from '@web-platform/homepage-features-homepage-hero';
import { HomepageVideoContainer } from '@web-platform/homepage-features-homepage-video';
import { enableWebToApp } from '@web-platform/homepage-utils-frontend-core';
import FlightsSearchControls from '@web-platform/shared-banana-features-components/src/FlightsSearchControlsGC/FlightsSearchControls';
import { GlobalHeader as BananaGlobalHeader } from '@web-platform/shared-banana-features-global-header';
import { GlobalTabs } from '@web-platform/shared-banana-features-global-tabs';
import { GoogleOneTap } from '@web-platform/shared-banana-features-google-one-tap';
import { LoginAutoPrompt } from '@web-platform/shared-banana-features-login-auto-prompt';
import {
  creativesLoaded,
  creativesQueryRequired,
} from '@web-platform/shared-banana-redux/src/actions/adverts';
import {
  getCreativesStatus,
  graphicPromos,
  homepageHeroes,
  homepageVideos,
} from '@web-platform/shared-banana-redux/src/state/combinedResults';
import { useMediaQuery } from '@web-platform/shared-banana-utils-frontend/src/helpers/hooks/useMediaQuery/useMediaQuery';
import {
  HOMEPAGE_FALLBACK_CARD_REASONS,
  SPONSORED_SERVICE_PRIORITY,
} from '@web-platform/shared-universal-constants/src/adverts';
import { SCREEN_NAMES } from '@web-platform/shared-universal-constants/src/screenName';
import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import { GlobalHeader } from '@web-platform/shared-universal-features-global-header';
import { getUserSearch } from '@web-platform/shared-universal-redux/src/state';
import { trackChokePoint } from '@web-platform/shared-universal-tracking/src/trackExperiment';
import { translate } from '@web-platform/shared-universal-translations';
import ErrorBoundary from '@web-platform/shared-universal-ui-error-boundary';
import {
  getConfigServiceValue,
  getFeatureDecision,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { lazyWithRetry } from '@web-platform/shared-universal-utils-frontend/src/helpers/lazyLoading';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { GLOBAL } from 'saddlebag-browser';

import SeoHeader from './SeoHeader';
import { useHomepageTracking } from './useHomepageTracking';

import STYLES from './Homepage.module.scss';

const HomepageArsCapabilityCreativeDataLazy = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "HomepageArsCapabilityCreativeData" */ './HomepageArsCapabilityCreativeData'
  ).then(({ HomepageArsCapabilityCreativeData }) => ({
    default: HomepageArsCapabilityCreativeData,
  })),
);

export type SCREEN_TYPE = 'home';

export const FooterLazy = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "Footer" */
      '@web-platform/shared-banana-features-components/src/Footer'
    ),
);

export const SeoComponentsLazy = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "SeoComponents" */
      '@web-platform/homepage-features-homepage-seo'
    ),
);

export const HomepageDropsLazy = lazyWithRetry(() =>
  import(/* webpackChunkName: "HomepageDrops" */ './HomepageDrops/HomepageDrops').then(
    ({ HomepageDrops }) => ({ default: HomepageDrops }),
  ),
);

//

const Home = () => {
  const dispatch = useDispatch();
  const allHomepageHeroes = useSelector(homepageHeroes);
  const allHomepageVideos = useSelector(homepageVideos);

  const showHomepageHero =
    Object.keys(allHomepageHeroes).length > 0 &&
    getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_HOMEPAGE_HERO);

  const isHomePageVideoAvailable = Object.keys(allHomepageVideos).length > 0;

  const showHomepageVideo =
    isHomePageVideoAvailable &&
    getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_HOMEPAGE_HERO_VIDEO);

  const isSmallScreen = useMediaQuery(BREAKPOINTS.SMALL_TABLET);

  const showHomepageAdCard = getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_GRAPHIC_PROMO);
  const showGlobalNavigation = getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_GLOBAL_NAVIGATION);
  const enableExploreEvents = getFeatureDecision(
    FEATURE_DECISION_KEYS.ENABLE_EXPLORE_BY_EXPERIENCE,
  );
  const eventsEntryVariant = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.EXPERIMENT.ENABLE_EVENTS_ENTRY,
  );

  const showHomepageDrops = getFeatureDecision(FEATURE_DECISION_KEYS.HOMEPAGE_DROPS_ENABLED);
  const enableGlobalHeaderGC = getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_BANANA_HEADER_G_C);

  useEffect(() => {
    trackChokePoint('homepage_exp_chokepoint');
    trackChokePoint('homepage_ars_capability');
    trackChokePoint('users_eligible_for_explore_events');
  }, []);

  if (isHomePageVideoAvailable) {
    trackChokePoint('homepage_video_ad_experiment_participant');
  }

  useHomepageTracking();

  useEffect(() => {
    const $window = GLOBAL.getWindow();

    const ads = $window.__internal?.homepageAds;
    if (!ads) {
      dispatch(creativesQueryRequired());
    } else {
      dispatch(creativesLoaded(JSON.parse(ads)));
      $window.__internal.homepageAds = null;
    }
  }, [dispatch]);

  const userSearch = useSelector(getUserSearch);
  const creativesStatus = useSelector(getCreativesStatus);
  const allGraphicPromos = useSelector(graphicPromos);

  useEffect(() => {
    if (creativesStatus !== 'ready' && creativesStatus !== 'error' && creativesStatus !== 'timeout')
      return;

    const { legs } = userSearch;
    const originName = legs[0]?.origin?.name || '';
    const destinationName = legs[0]?.destination?.name || '';

    const isPaidAdDisplayed = [
      ...Object.values(allGraphicPromos),
      ...Object.values(allHomepageHeroes),
      ...Object.values(allHomepageVideos),
    ].some(
      (ad) =>
        ad.analyticsProperties.priority === SPONSORED_SERVICE_PRIORITY.PREMIUM ||
        ad.analyticsProperties.priority === SPONSORED_SERVICE_PRIORITY.STANDARD,
    );

    enableWebToApp({ destinationName, isPaidAdDisplayed, originName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creativesStatus]);

  return useMemo(
    () => (
      <div className={STYLES.footerAnchorContainer}>
        <Suspense fallback={null}>
          <HomepageArsCapabilityCreativeDataLazy />
        </Suspense>
        <main className={STYLES.backgroundContainer} data-testid="banana-home-container">
          <SeoHeader />
          <div
            className={classNames(STYLES.searchControlsContainer, {
              [STYLES.searchControlsContainerHomepageHero]: showHomepageHero || showHomepageVideo,
            })}
          >
            <div className={STYLES.searchControlInnerContainer}>
              {enableGlobalHeaderGC ? (
                <div className={classNames(STYLES.headerWrapper, STYLES.headerWrapperGC)}>
                  <GlobalHeader />
                </div>
              ) : (
                <>
                  <div className={STYLES.headerWrapper}>
                    <BananaGlobalHeader theme="dark" showTabs={false} />
                  </div>
                  <div className={STYLES.headerTabsWrapper}>
                    <GlobalTabs screenName={SCREEN_NAMES.HOME_PAGE} />
                  </div>
                </>
              )}
              {!isSmallScreen && (
                <BpkBox marginTop={BpkSpacing.LG} marginBottom={BpkSpacing.XL}>
                  <BpkText
                    tagName="h2"
                    textStyle={TEXT_STYLES.heading2}
                    color={TEXT_COLORS.textPrimaryInverse}
                  >
                    {translate('homepage_strapline_v2')}
                  </BpkText>
                </BpkBox>
              )}
              <FlightsSearchControls supportParallelSearch />
              {showHomepageHero && (
                <div className={STYLES.homepageHero} data-testid="HomepageHero">
                  <HomepageHeroContainer />
                </div>
              )}
              {showHomepageVideo && (
                <div className={STYLES.homepageHero} data-testid="HomepageVideo">
                  <HomepageVideoContainer />
                </div>
              )}
            </div>
          </div>
          <div className={STYLES.navAndAdsWrapper} data-testid="homepage-background-content">
            {showGlobalNavigation && <GlobalNavigation parentPage="homepage" />}
            {showHomepageDrops && (
              <ErrorBoundary componentName="HomepageDrops" fallback={null}>
                <Suspense fallback={null}>
                  <HomepageDropsLazy />
                </Suspense>
              </ErrorBoundary>
            )}
            {enableExploreEvents && eventsEntryVariant === 'B' && <EventsGraphicPromo />}
            <div className={showGlobalNavigation ? '' : STYLES.adCardWithoutGlobalNav}>
              {showHomepageAdCard ? (
                <GraphicPromoCardController />
              ) : (
                <GraphicPromoFallbackCard
                  fallbackReason={HOMEPAGE_FALLBACK_CARD_REASONS.AD_DISABLED}
                />
              )}
            </div>
            <ErrorBoundary
              componentName="SeoComponentsLazy"
              fallback={null}
              onError={({ component, error }) =>
                logWarn({
                  component,
                  error,
                  message: 'A data error occurred while rendering SeoComponentsLazy.',
                })
              }
            >
              <Suspense fallback={null}>
                <SeoComponentsLazy />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
        <ErrorBoundary componentName="FooterLazy" fallback={null}>
          <Suspense fallback={null}>
            <FooterLazy />
          </Suspense>
        </ErrorBoundary>
        <GoogleOneTap />
        <ErrorBoundary componentName="LoginAutoPrompt" fallback={null}>
          <LoginAutoPrompt />
        </ErrorBoundary>
      </div>
    ),

    [
      isSmallScreen,
      showGlobalNavigation,
      showHomepageAdCard,
      showHomepageHero,
      showHomepageVideo,
      showHomepageDrops,
      enableExploreEvents,
      eventsEntryVariant,
      enableGlobalHeaderGC,
    ],
  );
};

// Required for dynamic import used by Webpack
export default Home;
